import React from "react";
import type { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { UnifiedSelect } from "@cmp-src/form/UnifiedSelect/UnifiedSelect";
import type { SelectItem } from "@cmp-src/form/UnifiedSelect/types";

export interface FloorSelectorProps {
  floors: IResource[];
  value?: string;
  onChangeFloor: (value: string) => void;
  className?: string;
  allowEmpty?: boolean;
  noFloorLabel?: string;
}

export const FloorSelector: React.FC<FloorSelectorProps> = ({
  floors,
  onChangeFloor,
  value,
  className,
  allowEmpty = false,
  noFloorLabel
}) => {
  // Convert floors to SelectItem format
  const items: SelectItem<IResource>[] = React.useMemo(() => {
    const floorItems: SelectItem<IResource>[] = floors.map((floor) => ({
      id: floor.id,
      name: floor.name,
      data: floor
    }));

    // Add empty option if allowEmpty is true
    if (allowEmpty) {
      return [
        {
          id: "0",
          name: noFloorLabel || "No floor"
        },
        ...floorItems
      ];
    }

    return floorItems;
  }, [floors, allowEmpty, noFloorLabel]);

  // Find the selected item based on value
  const selectedItem = React.useMemo(() => {
    if (value === undefined) {
      return null; // Show placeholder when value is undefined
    }
    if (value === "" || value === "0") {
      // If value is empty string or "0", select the empty option if allowEmpty is true
      return allowEmpty ? items.find((item) => item.id === "0") || null : null;
    }
    return items.find((item) => item.id === value) || null;
  }, [value, items, allowEmpty]);

  // Handle selection
  const handleSelect = (item: SelectItem<IResource> | null) => {
    if (!item) {
      onChangeFloor("");
      return;
    }
    // Convert "0" to empty string, otherwise pass the id
    onChangeFloor(item.id !== "0" ? item.id : "");
  };

  return (
    <div className={cn("w-[160px] md:mt-1", className)}>
      <UnifiedSelect
        items={items}
        value={selectedItem}
        onSelect={handleSelect}
        placeholder="Select floor"
        variant="default"
        triggerClassName={"w-full h-[42px]"}
      />
    </div>
  );
};
